import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, computed, inject, signal, viewChild } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, finalize, forkJoin, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { Loading } from '@components/ui/loading/loading';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { ExportButton } from '@components/button/export-button/export-button';
import { PrettyJsonPipe } from '@app/utils/pipes/pretty-json.pipe';
import { OrchestrationIdentityCellComponent } from '@app/pages/admin/components/orchestration-identity-cell/orchestration-identity-cell';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';
import { SubscriptionFormDialogComponent } from '@app/pages/admin/components/subscription-form-dialog/subscription-form-dialog';
import { SubscriptionScheduleGanttComponent } from '@app/pages/admin/components/subscription-schedule-gantt/subscription-schedule-gantt';
import { ScheduleGanttInput, ScheduleGanttWindowHours } from '@app/pages/admin/components/subscription-schedule-gantt/subscription-schedule-gantt.util';
import { QuartzCronLabelPipe } from '@app/pages/admin/orchestration-quartz-cron-label.pipe';
import { TimeZoneLabelPipe } from '@app/pages/admin/orchestration-time-zone-label.pipe';
import { DateFormatLangPipe } from '@app/utils/pipes/date-format-lang-pipe';
import { operationDetailsUrl } from '@app/pages/admin/operation-details.util';
import {
  OrchestrationIdentityLine,
  buildSubscriptionIdentityLines,
  buildTriggerIdentityLines,
  namedKeyLabel,
  orchestrationSubscriptionUrl,
  satelliteProductAnalysisUrlFromEvent,
} from '@app/pages/admin/orchestration-identity.util';
import { toSubscriptionProcessOptions } from '@app/pages/admin/process-subscription-form.util';
import { operatorCatalogProcesses } from '@app/pages/admin/orchestration-process-keys.util';
import {
  parseSubscriptionsCatalogQuery,
  serializeSubscriptionsCatalogQuery,
  subscriptionsCatalogQueryEqual,
  SubscriptionsCatalogView,
} from '@app/pages/admin/process-subscriptions-query.util';
import { OrchestrationTableHeadingComponent } from '@app/pages/admin/components/orchestration-table-heading/orchestration-table-heading';
import { filterByCatalogQuery } from '@app/pages/admin/orchestration-catalog-search.util';
import {
  PROCESS_GROUPS,
  ProcessGroupId,
  ProcessGroupDefinition,
  ProcessGroupFilterId,
  presentProcessGroups,
  processGroupId,
} from '@app/pages/admin/orchestration-process-groups.util';
import { ProcessSubscriptionService as ProcessSubscriptionApiService } from '@6grain/masofa-monolith-api-client/common/api/processSubscription.service';
import { ModuleSubscriptionsService, DisableModuleSubscriptionsResult } from '@app/services/module-subscriptions.service';
import { ProcessSubscriptionCatalogService } from '@app/services/process-subscription-catalog.service';
import { ProcessSubscriptionResponse } from '@6grain/masofa-monolith-api-client/common/model/processSubscriptionResponse';
import { ProcessEventTriggerService as ProcessEventTriggerApiService } from '@6grain/masofa-monolith-api-client/common/api/processEventTrigger.service';
import { ProcessEventTriggerResponse } from '@6grain/masofa-monolith-api-client/common/model/processEventTriggerResponse';
import { ProcessEventTriggerExecutionResponse } from '@6grain/masofa-monolith-api-client/common/model/processEventTriggerExecutionResponse';
import { SaveProcessEventTriggerRequest } from '@6grain/masofa-monolith-api-client/common/model/saveProcessEventTriggerRequest';

interface ProcessEventDefinition {
  key: string;
  displayName: string;
  description: string;
  samplePayloadJson: string;
}

interface ProcessDefinition {
  key: string;
  version: number;
  displayName?: string;
  description?: string;
}

interface EventTriggerForm {
  name: string;
  eventKey: string;
  sourceProcessDefinitionKey: string;
  targetProcessDefinitionKey: string;
  parameterTemplateJson: string;
  isActive: boolean;
}

interface SubscriptionRow {
  item: ProcessSubscriptionResponse;
  name: string;
  detailsUrl: string | null;
  identityLines: OrchestrationIdentityLine[];
}

interface EventTriggerRow {
  item: ProcessEventTriggerResponse;
  name: string;
  identityLines: OrchestrationIdentityLine[];
}

interface EventTriggerExecutionRow {
  item: ProcessEventTriggerExecutionResponse;
  sourceAssignmentUrl: string | null;
  targetAssignmentUrl: string | null;
  productAnalysisUrl: string | null;
}

@Component({
  selector: 'app-process-subscriptions-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    ButtonModule,
    DialogModule,
    TagModule,
    Loading,
    PrimaryButton,
    SecondaryButton,
    ExportButton,
    PrettyJsonPipe,
    OrchestrationIdentityCellComponent,
    SubscriptionFormDialogComponent,
    SubscriptionScheduleGanttComponent,
    QuartzCronLabelPipe,
    TimeZoneLabelPipe,
    DateFormatLangPipe,
    OrchestrationTableHeadingComponent,
    CopyableIdentifierComponent,
  ],
  templateUrl: './process-subscriptions-page.html',
  styleUrl: './process-subscriptions-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
})
export class ProcessSubscriptionsPageComponent implements OnInit, OnDestroy {
  private readonly service = inject(ProcessSubscriptionApiService);
  private readonly moduleSubscriptions = inject(ModuleSubscriptionsService);
  private readonly catalogService = inject(ProcessSubscriptionCatalogService);
  private readonly eventTriggerService = inject(ProcessEventTriggerApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly translate = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();
  private readonly formDialog = viewChild.required(SubscriptionFormDialogComponent);
  private openCreateFromQuery = this.route.snapshot.queryParamMap.get('create') === '1';
  private openCreateTriggerFromQuery = this.route.snapshot.queryParamMap.get('createTrigger') === '1';
  private openCopyId = this.route.snapshot.queryParamMap.get('copy');
  private openEditId = this.route.snapshot.queryParamMap.get('edit');

  readonly disablingModule = signal<ProcessGroupId | null>(null);
  readonly moduleDisableResult = signal<DisableModuleSubscriptionsResult | null>(null);
  readonly moduleDisableError = signal(false);
  readonly moduleActions = signal<(ProcessGroupDefinition & { activeCount: number })[]>([]);
  readonly moduleActiveCounts = computed(() => Object.fromEntries(
    this.moduleActions().map(group => [group.id, group.activeCount]),
  ) as Partial<Record<ProcessGroupId, number>>);

  processes: ProcessDefinition[] = [];
  private allSubscriptionRows: SubscriptionRow[] = [];
  private allEventTriggerRows: EventTriggerRow[] = [];
  subscriptionRows: SubscriptionRow[] = [];
  ganttItems: ScheduleGanttInput[] = [];
  ganttNow = new Date();
  eventDefinitions: ProcessEventDefinition[] = [];
  eventTriggerRows: EventTriggerRow[] = [];
  eventTriggerExecutions: EventTriggerExecutionRow[] = [];
  eventTriggerDialogVisible = false;
  eventTriggerAuditVisible = false;
  editingEventTrigger: ProcessEventTriggerResponse | null = null;
  selectedEventTrigger: ProcessEventTriggerResponse | null = null;
  loading = false;
  saving = false;
  catalogExportLoading = false;
  catalogImportLoading = false;
  errorMessage = '';
  catalogMessage = '';
  searchQuery = '';
  triggerSearchQuery = '';
  selectedView: SubscriptionsCatalogView = 'gantt';
  ganttWindowHours: ScheduleGanttWindowHours = 24;
  selectedGroup: ProcessGroupFilterId = 'all';
  groupChips: ProcessGroupDefinition[] = [];
  viewChips: { id: SubscriptionsCatalogView; label: string; selected: boolean }[] = [];
  emptySubscriptionsMessage = 'No process subscriptions found';
  emptyEventTriggersMessage = 'No event triggers found';
  readonly eventParameterPlaceholder = '{{event.assignmentId}}';
  eventTriggerForm: EventTriggerForm = this.emptyEventTriggerForm();

  ngOnInit(): void {
    this.rebuildViewChips();
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const parsed = parseSubscriptionsCatalogQuery(params);
      this.selectedView = parsed.view;
      this.ganttWindowHours = parsed.windowHours;
      this.selectedGroup = parsed.module;
      this.rebuildViewChips();
      this.applyCatalogSearch();
      this.cdr.markForCheck();
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCatalogSearch(query: string): void {
    this.searchQuery = query ?? '';
    this.applyCatalogSearch();
    this.cdr.markForCheck();
  }

  onTriggerSearch(query: string): void {
    this.triggerSearchQuery = query ?? '';
    this.applyCatalogSearch();
    this.cdr.markForCheck();
  }

  onGanttGroup(groupId: ProcessGroupFilterId): void {
    if (this.selectedGroup === groupId) {
      return;
    }
    this.selectedGroup = groupId;
    this.applyCatalogSearch();
    this.syncCatalogQuery();
    this.cdr.markForCheck();
  }

  onGanttWindow(hours: ScheduleGanttWindowHours): void {
    if (this.ganttWindowHours === hours) {
      return;
    }
    this.ganttWindowHours = hours;
    this.syncCatalogQuery();
  }

  selectCatalogView(view: SubscriptionsCatalogView): void {
    if (this.selectedView === view) {
      return;
    }
    this.selectedView = view;
    this.rebuildViewChips();
    this.syncCatalogQuery();
  }

  disableModuleSubscriptions(groupId: ProcessGroupId): void {
    if (this.disablingModule() !== null || this.loading || this.saving || this.catalogImportLoading) return;
    this.disablingModule.set(groupId);
    this.moduleDisableResult.set(null);
    this.moduleDisableError.set(false);
    this.moduleSubscriptions.disable(groupId).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.disablingModule.set(null)),
    ).subscribe({
      next: result => {
        this.moduleDisableResult.set(result);
        this.load();
      },
      error: () => this.moduleDisableError.set(true),
    });
  }

  load(): void {
    this.loading = true;
    this.errorMessage = '';
    forkJoin({
      processes: this.service.processSubscriptionsProcessesGet(),
      subscriptions: this.service.processSubscriptionsGet(),
      events: this.eventTriggerService.processEventTriggersEventsGet(),
      eventTriggers: this.eventTriggerService.processEventTriggersGet(),
    })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: ({ processes, subscriptions, events, eventTriggers }) => {
          this.processes = processes as ProcessDefinition[];
          this.eventDefinitions = events as ProcessEventDefinition[];
          this.allSubscriptionRows = subscriptions.map(item => this.toSubscriptionRow(item));
          this.allEventTriggerRows = eventTriggers.map(item => this.toEventTriggerRow(item));
          this.ganttNow = new Date();
          this.applyCatalogSearch();
          this.applyCreateQuery();
          this.applyCopyOrEditQuery();
          this.applyCreateTriggerQuery();
        },
        error: () => {
          this.errorMessage = 'Failed to load process subscriptions';
        },
      });
  }

  createEventTrigger(): void {
    this.editingEventTrigger = null;
    this.eventTriggerForm = this.emptyEventTriggerForm();
    this.eventTriggerForm.eventKey = this.eventDefinitions[0]?.key ?? '';
    this.eventTriggerForm.targetProcessDefinitionKey = this.processes[0]?.key ?? '';
    this.applyEventTemplate();
    this.eventTriggerDialogVisible = true;
  }

  editEventTrigger(trigger: ProcessEventTriggerResponse): void {
    this.editingEventTrigger = trigger;
    this.eventTriggerForm = {
      name: trigger.name ?? '',
      eventKey: trigger.eventKey ?? '',
      sourceProcessDefinitionKey: trigger.sourceProcessDefinitionKey ?? '',
      targetProcessDefinitionKey: trigger.targetProcessDefinitionKey ?? '',
      parameterTemplateJson: '',
      isActive: trigger.isActive ?? false,
    };
    this.eventTriggerDialogVisible = true;
  }

  onEventChanged(): void {
    if (this.eventTriggerForm.eventKey === 'assignment.completed' && !this.eventTriggerForm.sourceProcessDefinitionKey) {
      this.eventTriggerForm.sourceProcessDefinitionKey = this.processes[0]?.key ?? '';
    }
    if (!this.editingEventTrigger) this.applyEventTemplate();
  }

  saveEventTrigger(): void {
    let parameterTemplate: unknown | undefined;
    if (!this.editingEventTrigger || this.eventTriggerForm.parameterTemplateJson.trim()) {
      try {
        parameterTemplate = JSON.parse(this.eventTriggerForm.parameterTemplateJson);
      } catch {
        this.errorMessage = 'Parameter template must be valid JSON';
        return;
      }
    }
    const request: SaveProcessEventTriggerRequest = {
      name: this.eventTriggerForm.name.trim(),
      eventKey: this.eventTriggerForm.eventKey,
      sourceProcessDefinitionKey: this.eventTriggerForm.sourceProcessDefinitionKey || null,
      targetProcessDefinitionKey: this.eventTriggerForm.targetProcessDefinitionKey,
      isActive: this.eventTriggerForm.isActive,
      ...(parameterTemplate === undefined ? {} : { parameterTemplate }),
    };
    this.saving = true;
    const operation = this.editingEventTrigger
      ? this.eventTriggerService.processEventTriggersTriggerIdPut(this.editingEventTrigger.id!, request)
      : this.eventTriggerService.processEventTriggersPost(request);
    operation.pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.saving = false;
        this.cdr.markForCheck();
      }),
    ).subscribe({
      next: () => {
        this.eventTriggerDialogVisible = false;
        this.load();
      },
      error: () => {
        this.errorMessage = 'Failed to save event trigger';
      },
    });
  }

  showEventTriggerAudit(trigger: ProcessEventTriggerResponse): void {
    if (!trigger.id) return;
    this.selectedEventTrigger = trigger;
    this.eventTriggerExecutions = [];
    this.eventTriggerAuditVisible = true;
    this.eventTriggerService.processEventTriggersTriggerIdExecutionsGet(trigger.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: executions => {
        this.eventTriggerExecutions = executions.map(item => ({
          item,
          sourceAssignmentUrl: item.sourceAssignmentId
            ? operationDetailsUrl('Assignment', item.sourceAssignmentId)
            : null,
          targetAssignmentUrl: item.targetAssignmentId
            ? operationDetailsUrl('Assignment', item.targetAssignmentId)
            : null,
          productAnalysisUrl: satelliteProductAnalysisUrlFromEvent(trigger.eventKey, item.eventId),
        }));
        this.cdr.markForCheck();
      },
      error: () => {
        this.errorMessage = 'Failed to load event trigger audit';
        this.cdr.markForCheck();
      },
    });
  }

  removeEventTrigger(trigger: ProcessEventTriggerResponse): void {
    if (!trigger.id) return;
    if (!window.confirm(this.translate.instant('Delete event trigger confirmation', { name: trigger.name }))) return;
    this.eventTriggerService.processEventTriggersTriggerIdDelete(trigger.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.load(),
      error: () => {
        this.errorMessage = 'Failed to delete event trigger';
        this.cdr.markForCheck();
      },
    });
  }

  create(processKey?: string): void {
    if (this.disablingModule() !== null) return;
    this.formDialog().openCreate(toSubscriptionProcessOptions(operatorCatalogProcesses(this.processes)), processKey);
  }

  exportCatalog(): void {
    this.catalogExportLoading = true;
    this.errorMessage = '';
    this.catalogService.exportCatalog()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.catalogExportLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: blob => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'process-subscriptions.json';
          link.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.errorMessage = 'Failed to export process subscriptions';
        },
      });
  }

  onCatalogFileSelected(event: Event): void {
    if (this.disablingModule() !== null) return;
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }

    this.catalogImportLoading = true;
    this.errorMessage = '';
    this.catalogMessage = '';
    file.text()
      .then(text => {
        const document = JSON.parse(text) as unknown;
        this.catalogService.importCatalog(document)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => {
              this.catalogImportLoading = false;
              this.cdr.markForCheck();
            }),
          )
          .subscribe({
            next: result => {
              this.catalogMessage = this.translate.instant(
                'Imported {{created}} created, {{updated}} updated',
                {
                  created: result.created ?? 0,
                  updated: result.updated ?? 0,
                },
              );
              if (result.errors?.length) {
                this.errorMessage = result.errors.map(item => item.message).filter(Boolean).join('; ');
              }
              this.load();
            },
            error: () => {
              this.errorMessage = 'Failed to import process subscriptions';
            },
          });
      })
      .catch(() => {
        this.catalogImportLoading = false;
        this.errorMessage = 'Failed to import process subscriptions';
        this.cdr.markForCheck();
      });
  }

  private applyCreateTriggerQuery(): void {
    if (!this.openCreateTriggerFromQuery) {
      return;
    }
    this.openCreateTriggerFromQuery = false;
    this.createEventTrigger();
    const eventKey = this.route.snapshot.queryParamMap.get('eventKey');
    if (eventKey && this.eventDefinitions.some(event => event.key === eventKey)) {
      this.eventTriggerForm.eventKey = eventKey;
      if (eventKey === 'assignment.completed' && !this.eventTriggerForm.sourceProcessDefinitionKey) {
        this.eventTriggerForm.sourceProcessDefinitionKey = this.processes[0]?.key ?? '';
      }
      this.applyEventTemplate();
    }
  }

  private applyCreateQuery(): void {
    if (!this.openCreateFromQuery) {
      return;
    }
    this.openCreateFromQuery = false;
    const processKey = this.route.snapshot.queryParamMap.get('processKey');
    this.create(processKey && this.processes.some(process => process.key === processKey) ? processKey : undefined);
  }

  private applyCopyOrEditQuery(): void {
    if (this.openCopyId) {
      const copyId = this.openCopyId;
      this.openCopyId = null;
      const found = this.subscriptionRows.find(row => row.item.id === copyId);
      if (found) {
        this.copy(found.item);
      }
      return;
    }
    if (this.openEditId) {
      const editId = this.openEditId;
      this.openEditId = null;
      const found = this.subscriptionRows.find(row => row.item.id === editId);
      if (found) {
        this.edit(found.item);
      }
    }
  }

  edit(subscription: ProcessSubscriptionResponse): void {
    if (this.disablingModule() !== null) return;
    this.formDialog().openEdit(toSubscriptionProcessOptions(this.processes), subscription);
  }

  copy(subscription: ProcessSubscriptionResponse): void {
    if (this.disablingModule() !== null) return;
    this.formDialog().openCopy(toSubscriptionProcessOptions(this.processes), subscription);
  }

  onSubscriptionSaved(): void {
    this.load();
  }

  runNow(subscription: ProcessSubscriptionResponse): void {
    if (this.disablingModule() !== null) return;
    if (!subscription.id) return;
    this.service.processSubscriptionsSubscriptionIdRunPost(subscription.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.load(),
      error: () => {
        this.errorMessage = 'Failed to run process subscription';
        this.cdr.markForCheck();
      },
    });
  }

  remove(subscription: ProcessSubscriptionResponse): void {
    if (this.disablingModule() !== null) return;
    if (!subscription.id) return;
    if (!window.confirm(this.translate.instant('Delete subscription confirmation', { name: subscription.name }))) {
      return;
    }
    this.service.processSubscriptionsSubscriptionIdDelete(subscription.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.load(),
      error: () => {
        this.errorMessage = 'Failed to delete process subscription';
        this.cdr.markForCheck();
      },
    });
  }

  private toGanttItem(row: SubscriptionRow): ScheduleGanttInput {
    return {
      id: row.item.id ?? row.name,
      name: row.name,
      processKey: row.item.processDefinitionKey ?? '',
      processLabel: namedKeyLabel(this.processes, row.item.processDefinitionKey),
      cronExpression: row.item.cronExpression ?? '',
      timeZoneId: row.item.timeZoneId ?? 'UTC',
      isActive: row.item.isActive !== false,
      nextFireAt: row.item.nextFireAt ?? null,
      detailsUrl: row.detailsUrl,
    };
  }

  private toSubscriptionRow(item: ProcessSubscriptionResponse): SubscriptionRow {
    return {
      item,
      name: item.name ?? '—',
      detailsUrl: orchestrationSubscriptionUrl(item.id),
      identityLines: buildSubscriptionIdentityLines({
        processKey: item.processDefinitionKey,
        processLabel: namedKeyLabel(this.processes, item.processDefinitionKey),
      }),
    };
  }

  private toEventTriggerRow(item: ProcessEventTriggerResponse): EventTriggerRow {
    return {
      item,
      name: item.name ?? '—',
      identityLines: buildTriggerIdentityLines({
        eventKey: item.eventKey,
        eventLabel: namedKeyLabel(this.eventDefinitions, item.eventKey),
        sourceProcessKey: item.sourceProcessDefinitionKey,
        sourceProcessLabel: namedKeyLabel(this.processes, item.sourceProcessDefinitionKey),
        targetProcessKey: item.targetProcessDefinitionKey,
        targetProcessLabel: namedKeyLabel(this.processes, item.targetProcessDefinitionKey),
      }),
    };
  }

  private applyCatalogSearch(): void {
    const previousGroup = this.selectedGroup;
    this.moduleActions.set(PROCESS_GROUPS.flatMap(group => {
      const rows = this.allSubscriptionRows.filter(row => processGroupId(row.item.processDefinitionKey ?? '') === group.id);
      return rows.length ? [{ ...group, activeCount: rows.filter(row => row.item.isActive).length }] : [];
    }));
    const searchedSubscriptions = filterByCatalogQuery(
      this.allSubscriptionRows,
      this.searchQuery,
      row => [
        row.name,
        this.translate.instant(row.name),
        row.item.cronExpression,
        row.item.processDefinitionKey,
        ...row.identityLines.flatMap(line => [line.label, line.title]),
      ],
    );
    this.groupChips = presentProcessGroups(
      searchedSubscriptions.map(row => row.item.processDefinitionKey ?? ''),
    );
    if (
      this.selectedGroup !== 'all'
      && this.allSubscriptionRows.length > 0
      && !this.groupChips.some(chip => chip.id === this.selectedGroup)
    ) {
      this.selectedGroup = 'all';
    }
    this.subscriptionRows = this.selectedGroup === 'all'
      ? searchedSubscriptions
      : searchedSubscriptions.filter(row =>
        processGroupId(row.item.processDefinitionKey ?? '') === this.selectedGroup,
      );
    this.eventTriggerRows = filterByCatalogQuery(
      this.allEventTriggerRows,
      this.triggerSearchQuery,
      row => [
        row.name,
        this.translate.instant(row.name),
        row.item.eventKey,
        row.item.sourceProcessDefinitionKey,
        row.item.targetProcessDefinitionKey,
        ...row.identityLines.flatMap(line => [line.label, line.title]),
      ],
    );
    this.ganttItems = this.subscriptionRows.map(row => this.toGanttItem(row));
    this.emptySubscriptionsMessage = this.searchQuery.trim() ? 'No catalog matches' : 'No process subscriptions found';
    this.emptyEventTriggersMessage = this.triggerSearchQuery.trim() ? 'No catalog matches' : 'No event triggers found';
    if (this.selectedGroup !== previousGroup) {
      this.syncCatalogQuery();
    }
  }

  private catalogQuery() {
    return {
      view: this.selectedView,
      windowHours: this.ganttWindowHours,
      module: this.selectedGroup,
    };
  }

  private rebuildViewChips(): void {
    this.viewChips = [
      { id: 'subscriptions', label: 'Scheduled subscriptions', selected: this.selectedView === 'subscriptions' },
      { id: 'gantt', label: 'Schedule Gantt', selected: this.selectedView === 'gantt' },
    ];
  }

  private syncCatalogQuery(): void {
    const next = this.catalogQuery();
    const current = parseSubscriptionsCatalogQuery(this.route.snapshot.queryParamMap);
    if (subscriptionsCatalogQueryEqual(current, next)) {
      return;
    }
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: serializeSubscriptionsCatalogQuery(next),
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private emptyEventTriggerForm(): EventTriggerForm {
    return {
      name: '',
      eventKey: '',
      sourceProcessDefinitionKey: '',
      targetProcessDefinitionKey: '',
      parameterTemplateJson: '{}',
      isActive: true,
    };
  }

  private applyEventTemplate(): void {
    if (this.eventTriggerForm.eventKey === 'satellite.product-downloaded'
      && this.eventTriggerForm.targetProcessDefinitionKey === 'satellite.create-product-postprocessing') {
      this.eventTriggerForm.parameterTemplateJson = `{
  "satelliteProductId": "{{event.satelliteProductId}}",
  "priority": "{{event.priority}}"
}`;
      return;
    }
    this.eventTriggerForm.parameterTemplateJson = '{}';
  }

  currentEventDefinition(): ProcessEventDefinition | undefined {
    return this.eventDefinitions.find(event => event.key === this.eventTriggerForm.eventKey);
  }

}
